import React from 'react';
import { styleToCss } from './richtext/nodes';
import {
  DesignerDocument, DesignerNode, HEADING_SIZES, ListItem, MarkFlags, SignatureParty, TextMark,
  listMarkerLabel, markRuns,
} from './richtext/model';
import { clampPerRow, DEFAULT_SIGN_HEIGHT, signatureRows } from './richtext/signatureBlock';

/** Zeichenformat → CSS. Spiegelt die Darstellung im Editor und im PDF. */
function markCss(flags: MarkFlags): React.CSSProperties {
  const decoration = [flags.underline ? 'underline' : '', flags.strike ? 'line-through' : ''].filter(Boolean).join(' ');
  return {
    fontWeight: flags.bold ? 700 : undefined,
    fontStyle: flags.italic ? 'italic' : undefined,
    textDecoration: decoration || (flags.link ? 'underline' : undefined),
    color: flags.link && !flags.color ? '#2563eb' : flags.color,
    background: flags.background,
    fontSize: flags.fontSize ? `${flags.fontSize}pt` : undefined,
    fontFamily: flags.fontFamily === 'Times-Roman' ? 'Georgia, "Times New Roman", serif'
      : flags.fontFamily === 'Courier' ? 'ui-monospace, Menlo, monospace'
      : flags.fontFamily ? 'Helvetica, Arial, sans-serif' : undefined,
    fontVariant: flags.smallCaps ? 'small-caps' : undefined,
    letterSpacing: flags.letterSpacing ? `${flags.letterSpacing}px` : undefined,
    verticalAlign: flags.superscript ? 'super' : flags.subscript ? 'sub' : undefined,
    ...(flags.superscript || flags.subscript ? { fontSize: `${(flags.fontSize || 10) * 0.75}pt` } : {}),
  };
}

/** Text mit allen Zeichenformaten – Grundlage aller Vorschauen. */
function FormattedText({ text, marks }: { text: string; marks?: TextMark[] }) {
  if (!text) return <span className="text-gray-300">(leer)</span>;
  return (
    <>
      {markRuns(text, marks || []).map(run => (
        <span key={`${run.from}-${run.to}`} style={markCss(run.flags)}>{text.slice(run.from, run.to)}</span>
      ))}
    </>
  );
}

function PreviewNode({ document, node }: { document: DesignerDocument; node: DesignerNode }) {
  const css = styleToCss(document, node.style);

  if (node.type === 'paragraph' || node.type === 'heading') {
    const heading = node.type === 'heading';
    const level = node.level || 1;
    return (
      <div
        style={{
          ...css,
          whiteSpace: 'pre-wrap',
          fontSize: heading ? `${node.style?.fontSize || HEADING_SIZES[level] || 16}pt` : css.fontSize,
          fontWeight: heading ? 700 : css.fontWeight,
        }}
      >
        <FormattedText text={node.text || ''} marks={node.marks} />
      </div>
    );
  }

  if (node.type === 'list') {
    const items: ListItem[] = node.items || [];
    return (
      <div style={css}>
        {items.map((item, index) => (
          <div key={item.id} className="flex gap-2" style={{ paddingLeft: (item.level || 0) * 16, whiteSpace: 'pre-wrap' }}>
            <span className="select-none text-gray-500 shrink-0">{listMarkerLabel(node, index)}</span>
            <span className="flex-1"><FormattedText text={item.text || ''} marks={item.marks} /></span>
          </div>
        ))}
      </div>
    );
  }

  if (node.type === 'table') {
    const columns: any[] = node.columns || [];
    const rows: any[] = node.rows || [];
    return (
      <div className="overflow-x-auto" style={css}>
        <table className="min-w-full border border-gray-300 text-left">
          {node.headerRow !== false && (
            <thead className="bg-gray-100">
              <tr>{columns.map(column => (
                <th key={column.id} className="border border-gray-300 px-2 py-1 font-medium" style={{ width: column.width ? `${column.width}%` : undefined }}>{column.label}</th>
              ))}</tr>
            </thead>
          )}
          <tbody>
            {rows.map(row => (
              <tr key={row.id}>
                {columns.map(column => (
                  <td key={column.id} className="border border-gray-300 px-2 py-1 align-top whitespace-pre-wrap">{String(row.cells?.[column.key] ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (node.type === 'image') {
    return (
      <div style={{ ...css, textAlign: node.style?.align || 'left' }}>
        <img src={node.src} alt={node.alt || node.caption || 'Bild'} style={{ width: node.width, height: node.height, maxWidth: '100%' }} />
        {node.caption && <div className="text-xs text-gray-500 mt-1">{node.caption}</div>}
      </div>
    );
  }

  if (node.type === 'signatureBlock') {
    const parties: SignatureParty[] = node.parties || [];
    const perRow = clampPerRow(node.perRow);
    const signHeight = node.signHeight ?? DEFAULT_SIGN_HEIGHT;
    const rows = signatureRows(parties, perRow);
    return (
      <table style={css} className="w-full table-fixed border-collapse">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: perRow }, (_unused, columnIndex) => {
                const party = row[columnIndex];
                return (
                  <td
                    key={party?.id || `leer-${columnIndex}`}
                    className="align-top"
                    style={{ paddingRight: columnIndex < perRow - 1 ? 16 : 0, paddingBottom: rowIndex < rows.length - 1 ? signHeight / 2 + 8 : 0 }}
                  >
                    {party && (
                      <>
                        <div style={{ height: signHeight }} className="border-b border-gray-800" />
                        <div className="mt-1 text-center">{party.name}</div>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (node.type === 'divider') return <div style={{ ...css, borderTop: css.borderTop || '1px solid #d1d5db' }} />;
  if (node.type === 'spacer') return <div style={{ height: node.height || 24 }} />;
  if (node.type === 'pageBreak') {
    return (
      <div className="flex items-center gap-2 my-2 text-[11px] uppercase tracking-wide text-primary-600">
        <span className="flex-1 border-t border-dashed border-primary-300" />Seitenumbruch<span className="flex-1 border-t border-dashed border-primary-300" />
      </div>
    );
  }
  if (node.type === 'container') {
    return (
      <div style={{ ...css, display: 'flex', gap: node.style?.columnGap ?? 12 }}>
        {(node.columns || []).map((column: any) => (
          <div key={column.id} style={{ width: `${column.width}%` }}>
            {(column.nodes || []).map((child: DesignerNode) => <PreviewNode key={child.id} document={document} node={child} />)}
          </div>
        ))}
      </div>
    );
  }
  return <div className="text-xs text-gray-500">{node.type}</div>;
}

/**
 * Schreibgeschützte Anzeige eines strukturierten Dokuments – für Vorschauen
 * (z. B. beim RTF-Import) ohne den vollständigen Editor.
 */
export default function DocumentPreview({ document, className }: { document: DesignerDocument; className?: string }) {
  return (
    <div className={className}>
      {(document.nodes || []).map(node => <PreviewNode key={node.id} document={document} node={node} />)}
    </div>
  );
}
